import { Container, Graphics } from 'pixi.js';
import { TILE_SIZE } from '../main.js';
import { WALKABLE_TILES } from '../systems/DungeonGenerator.js';

/**
 * Projectile type configurations (M19).
 * Each type defines speed, damage, visual properties, piercing, and optional status effects.
 */
export const PROJECTILE_TYPES = {
  arrow: { speed: 120, damage: 10, size: 4, color: 0xcccccc, piercing: false },
  magic_bolt: { speed: 100, damage: 15, size: 5, color: 0xaa44ff, piercing: false },
  fire_bolt: { speed: 90, damage: 12, size: 5, color: 0xff4400, piercing: false, statusEffect: 'burning' },
  ice_shard: { speed: 110, damage: 8, size: 4, color: 0x88ccff, piercing: false, statusEffect: 'frozen' },
  bone_shard: { speed: 80, damage: 8, size: 3, color: 0xccbb99, piercing: true },
  poison_bolt: { speed: 100, damage: 5, size: 4, color: 0x44cc44, piercing: false, statusEffect: 'poisoned' },
};

/**
 * Simple projectile entity.
 * Travels in a direction at a given speed; destroyed on wall hit or target hit.
 * Supports typed projectiles with piercing and status effects (M19).
 */
export class Projectile {
  /**
   * @param {number} x - Start X pixel position
   * @param {number} y - Start Y pixel position
   * @param {number} dx - Direction X (normalized)
   * @param {number} dy - Direction Y (normalized)
   * @param {number} speed - Pixels per second
   * @param {number} damage - Damage dealt on hit
   * @param {boolean} fromEnemy - True if fired by an enemy, false if fired by player
   * @param {string} [projectileType] - Key from PROJECTILE_TYPES (optional)
   */
  constructor(x, y, dx, dy, speed, damage, fromEnemy = true, projectileType = null) {
    this.x = x;
    this.y = y;
    this.dx = dx;
    this.dy = dy;
    this.fromEnemy = fromEnemy;
    this.alive = true;

    // Apply projectile type configuration if provided
    this.projectileType = projectileType;
    const typeConfig = projectileType ? PROJECTILE_TYPES[projectileType] : null;

    this.speed = typeConfig ? typeConfig.speed : speed;
    this.damage = typeConfig ? typeConfig.damage : damage;
    this.piercing = typeConfig ? typeConfig.piercing : false;
    this.statusEffect = typeConfig ? (typeConfig.statusEffect || null) : null;

    const size = typeConfig ? typeConfig.size : 2;
    const color = typeConfig ? typeConfig.color : (fromEnemy ? 0xff4444 : 0xffffff);

    // Track how many entities this piercing projectile has passed through
    this.pierceCount = 0;
    this.maxPierces = 1; // passes through first target

    // Set of entities already hit (to prevent double-hitting on pierce)
    this.hitEntities = new Set();

    // Max lifetime to prevent infinite projectiles
    this.lifetime = 5.0;

    this.container = new Container();
    this.gfx = new Graphics();
    this.gfx.circle(0, 0, size);
    this.gfx.fill(color);
    this.container.addChild(this.gfx);
    this._syncVisual();
  }

  /**
   * Update projectile position.
   * @param {number} dt - Delta time in seconds
   * @param {number[][]} tiles - Dungeon tile grid
   * @returns {boolean} False if projectile hit a wall and should be destroyed
   */
  update(dt, tiles) {
    if (!this.alive) return false;

    this.lifetime -= dt;
    if (this.lifetime <= 0) {
      this.alive = false;
      return false;
    }

    const newX = this.x + this.dx * this.speed * dt;
    const newY = this.y + this.dy * this.speed * dt;

    // Check wall collision at new position
    const col = Math.floor(newX / TILE_SIZE);
    const row = Math.floor(newY / TILE_SIZE);
    const height = tiles.length;
    const width = tiles[0].length;

    if (row < 0 || row >= height || col < 0 || col >= width || !WALKABLE_TILES.has(tiles[row][col])) {
      this.alive = false;
      return false;
    }

    this.x = newX;
    this.y = newY;
    this._syncVisual();
    return true;
  }

  /**
   * Check collision with a target entity.
   * For piercing projectiles, tracks which entities have been hit.
   * @param {object} target - Entity with x, y, size properties
   * @returns {boolean} True if projectile hit the target
   */
  checkHit(target) {
    if (!this.alive) return false;

    // Skip already-hit entities for piercing projectiles
    if (this.hitEntities.has(target)) return false;

    const targetCX = target.x + TILE_SIZE / 2;
    const targetCY = target.y + TILE_SIZE / 2;
    const targetRadius = (target.size || 14) / 2;

    const dx = this.x - targetCX;
    const dy = this.y - targetCY;
    const dist = Math.sqrt(dx * dx + dy * dy);

    if (dist <= targetRadius + 3) {
      // Mark this entity as hit
      this.hitEntities.add(target);

      // For piercing projectiles, check if we should keep going
      if (this.piercing) {
        this.pierceCount++;
        if (this.pierceCount > this.maxPierces) {
          // Exceeded max pierces, projectile dies
          this.alive = false;
        }
        // Projectile stays alive if pierceCount <= maxPierces
      } else {
        // Non-piercing: projectile dies on first hit
        this.alive = false;
      }

      return true;
    }

    return false;
  }

  _syncVisual() {
    this.container.x = Math.round(this.x);
    this.container.y = Math.round(this.y);
  }
}
